import { useQueryClient, useMutation, type UseMutationOptions } from '@tanstack/vue-query';
import { useI18n } from 'vue-i18n';

import {
  YardTaskCheckLocationOutcome,
  type AssetContainerEventType,
  type AssetContainerState
} from '@nova/core';
import { queryKeys, taskKeys } from '@satellite/composables/queryKeys';
import yardTaskService from '@satellite/services/yard-tasks-service';
import assetVisitService from '@satellite/services/asset-visit-service';
import yardSpotService from '@satellite/services/yardSpot-service';
import useNotify from '@/composables/useNotify';

import type { NotifyFn } from '@/lib/notify';

type QueryClient = ReturnType<typeof useQueryClient>;

type MutationCallbacks<TVariables> = Pick<
  UseMutationOptions<unknown, unknown, TVariables>,
  'onSuccess' | 'onError'
>;

function buildHandlers<TVariables extends { taskId: string }>(
  queryClient: QueryClient,
  callbacks: MutationCallbacks<TVariables> | undefined
) {
  return {
    onSuccess: async (...args: [unknown, TVariables, unknown]) => {
      const [, variables] = args;
      await queryClient.invalidateQueries({ queryKey: taskKeys.detail(variables.taskId) });
      await queryClient.invalidateQueries({ queryKey: taskKeys.list() });
      // Refresh the inventory-check BFF listing for this task so the "open inventory" panel,
      // title counters and completion gate reflect the new outcome immediately (same tab); the
      // Subspace round-trip covers other tabs. No-op when no inventory query is mounted.
      await queryClient.invalidateQueries({
        queryKey: queryKeys.yardInventoryCheck(variables.taskId)
      });
      await queryClient.invalidateQueries({ queryKey: queryKeys.task(variables.taskId) });
      callbacks?.onSuccess?.(...args);
    },
    onError: (...args: [unknown, TVariables, unknown]) => {
      callbacks?.onError?.(...args);
    }
  };
}

function withNotify<TVariables>(
  notify: NotifyFn,
  options: {
    successMessage: string | ((variables: TVariables) => string);
    errorMessage: string;
    callbacks?: MutationCallbacks<TVariables>;
  }
): MutationCallbacks<TVariables> {
  const { successMessage, errorMessage, callbacks } = options;
  return {
    onSuccess: (...args: [unknown, TVariables, unknown]) => {
      const [, variables] = args;
      notify(
        typeof successMessage === 'function' ? successMessage(variables) : successMessage,
        'success'
      );
      callbacks?.onSuccess?.(...args);
    },
    onError: (...args: [unknown, TVariables, unknown]) => {
      notify(errorMessage, 'error');
      callbacks?.onError?.(...args);
    }
  };
}

type RecordOutcomeVariables = {
  taskId: string;
  warehouseId: string;
  yardTaskCheckId: string;
  assetContainerId: string;
  outcome: YardTaskCheckLocationOutcome;
};

export function useRecordCheckAssetLocationOutcomeMutation(
  options?: MutationCallbacks<RecordOutcomeVariables>
) {
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const { notify } = useNotify();
  return useMutation<unknown, unknown, RecordOutcomeVariables>({
    mutationFn: ({ warehouseId, yardTaskCheckId, assetContainerId, outcome }) =>
      yardTaskService.createCheckAssetContainerOutcome({
        warehouseId,
        yardTaskCheckId,
        assetContainerId,
        outcome
      }),
    ...buildHandlers(
      queryClient,
      withNotify(notify, {
        successMessage: ({ outcome }) =>
          t(
            outcome === YardTaskCheckLocationOutcome.CONFIRMED
              ? 'assetCheckWizard.success.locationConfirmed'
              : 'assetCheckWizard.success.locationMissing'
          ),
        errorMessage: t('assetCheckWizard.errors.confirmedError'),
        callbacks: options
      })
    )
  });
}

type ConfirmOutcomeVariables = {
  taskId: string;
  warehouseId: string;
  yardTaskCheckId: string;
  assetContainerId: string;
};

/** Always records a CONFIRMED location outcome. */
export function useConfirmCheckAssetLocationMutation(
  options?: MutationCallbacks<ConfirmOutcomeVariables>
) {
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const { notify } = useNotify();
  return useMutation<unknown, unknown, ConfirmOutcomeVariables>({
    mutationFn: ({ warehouseId, yardTaskCheckId, assetContainerId }) =>
      yardTaskService.createCheckAssetContainerOutcome({
        warehouseId,
        yardTaskCheckId,
        assetContainerId,
        outcome: YardTaskCheckLocationOutcome.CONFIRMED
      }),
    ...buildHandlers(
      queryClient,
      withNotify(notify, {
        successMessage: t('assetCheckWizard.success.locationConfirmed'),
        errorMessage: t('assetCheckWizard.errors.confirmedError'),
        callbacks: options
      })
    )
  });
}

type ContainerEventVariables = {
  taskId: string;
  assetContainerId: string;
  eventType: AssetContainerEventType;
};

/** POST /asset-container/event — wizard step 2. */
export function useCreateAssetContainerEventMutation(
  options?: MutationCallbacks<ContainerEventVariables>
) {
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const { notify } = useNotify();
  return useMutation<unknown, unknown, ContainerEventVariables>({
    mutationFn: ({ assetContainerId, eventType }) =>
      assetVisitService.updateAssetContainerStatus(assetContainerId, eventType),
    ...buildHandlers(
      queryClient,
      withNotify(notify, {
        successMessage: t('assetCheckWizard.success.statusSaved'),
        errorMessage: t('assetCheckWizard.errors.eventError'),
        callbacks: options
      })
    )
  });
}

type ReassignSpotAssignmentVariables = {
  taskId: string;
  assignmentId: string;
  spotId: string;
};

/** PATCH /yard/spot-assignment/:id/reassign — step 1 update-location when asset has an active assignment. */
export function useReassignSpotAssignmentMutation(
  options?: MutationCallbacks<ReassignSpotAssignmentVariables>
) {
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const { notify } = useNotify();
  return useMutation<unknown, unknown, ReassignSpotAssignmentVariables>({
    mutationFn: ({ assignmentId, spotId }) =>
      yardSpotService.reassignSpotAssignment(assignmentId, { spotId }),
    ...buildHandlers(
      queryClient,
      withNotify(notify, {
        successMessage: t('assetCheckWizard.success.locationUpdated'),
        errorMessage: t('assetCheckWizard.errors.reassignError'),
        callbacks: options
      })
    )
  });
}

type CreateSpotAssignmentVariables = {
  taskId: string;
  assetContainerId: string;
  spotId: string;
};

/** POST /yard/spot-assignment — step 1 update-location when asset has no active assignment yet. */
export function useCreateSpotAssignmentMutation(
  options?: MutationCallbacks<CreateSpotAssignmentVariables>
) {
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const { notify } = useNotify();
  return useMutation<unknown, unknown, CreateSpotAssignmentVariables>({
    mutationFn: ({ assetContainerId, spotId }) =>
      yardSpotService.createSpotAssignment({ assetContainerId, spotId }),
    ...buildHandlers(
      queryClient,
      withNotify(notify, {
        successMessage: t('assetCheckWizard.success.locationUpdated'),
        errorMessage: t('assetCheckWizard.errors.reassignError'),
        callbacks: options
      })
    )
  });
}

type PatchContainerVariables = {
  taskId: string;
  assetContainerId: string;
  state: AssetContainerState;
};

/** PATCH /asset-container/:id — wizard step 3. */
export function usePatchAssetContainerMutation(
  options?: MutationCallbacks<PatchContainerVariables>
) {
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const { notify } = useNotify();
  return useMutation<unknown, unknown, PatchContainerVariables>({
    mutationFn: ({ assetContainerId, state }) =>
      assetVisitService.updateAssetContainer(assetContainerId, { state }),
    ...buildHandlers(
      queryClient,
      withNotify(notify, {
        successMessage: t('assetCheckWizard.success.stateSaved'),
        errorMessage: t('assetCheckWizard.errors.stateError'),
        callbacks: options
      })
    )
  });
}
